import { defineDocument } from './types.js'
import {
  ALL_TABS, BASIC_ACTIONS, SYNCED_BASIC_ACTIONS, baseColumns, baseFilters, col, f, interstate, jobType,
  opt, quantityLines, stockListExtras, taxUnit, taxUnitChip,
  OURS, ofPartyOrOurs, scoped,
} from './common.js'
import type { ColumnDef, FilterDef } from './types.js'
import { many } from './lookup-narrowing.js'

// The reference's own wording, Title Case and all (STK-007).
const TRANSFER_TYPES: ReadonlyArray<[string, string]> = [
  ['WAREHOUSE_TO_SITE', 'Warehouse to Site'],
  ['SITE_TO_WAREHOUSE', 'Site to Warehouse'],
  ['SITE_TO_SITE', 'Site to Site'],
  ['RESERVED_TO_UNRESERVED', 'Reserved to Unreserved'],
]
/** Every type with a warehouse end — all but site to site. A blank type is warehouse to site. */
const WAREHOUSE_ENDED = ['', 'WAREHOUSE_TO_SITE', 'SITE_TO_WAREHOUSE', 'RESERVED_TO_UNRESERVED'] as const
const RECORD_TYPES: ReadonlyArray<[string, string]> = [['ISSUE', 'Issue from store'], ['RETURN', 'Return to store']]
const ADJUSTMENT_TYPES: ReadonlyArray<[string, string]> = [['INCREASE', 'Increase'], ['DECREASE', 'Decrease']]
const choices = (pairs: ReadonlyArray<[string, string]>) => pairs.map(([value, label]) => ({ value, label }))

/** Who pressed Submit, and the GSTIN it went out under: filters every stock list offers. */
const submittedBy: FilterDef = { key: 'submittedById', label: 'Submitted by', control: 'lookup', lookup: { entity: 'employee' } }
const taxUnitFilter: FilterDef = { key: 'taxUnitId', label: 'Tax unit', control: 'lookup', lookup: { entity: 'tax_unit' } }
const jobTypeFilter: FilterDef = { key: 'jobTypeId', label: 'Job type', control: 'lookup', lookup: { entity: 'job_type' } }
const typeColumn = (key: string): ColumnDef => ({ key, label: 'Type', type: 'text', width: 170 })
/** The reference's stock list heads (STK-017): "Document Number", "Document Date". */
const docNoColumn: ColumnDef = { key: 'documentNo', label: 'Document number', type: 'docno', width: 152 }
const docDateColumn: ColumnDef = { key: 'documentDate', label: 'Document date', type: 'date', width: 128 }
const totalColumn = (optional = false): ColumnDef =>
  ({ key: 'total', label: 'Total (FCY)', type: 'money', width: 150, align: 'right', ...(optional ? { optional: true } : {}) })
/** The stock lists' own filters, in the reference's order: number and date first, status last. */
const docNoFilter: FilterDef = { key: 'documentNo', label: 'Document number', control: 'text' }
const docDateFilter: FilterDef = { key: 'documentDate', label: 'Document date', control: 'dateRange' }
const statusFilter: FilterDef = { key: 'documentStatus', label: 'Status', control: 'status' }
const projectFilter: FilterDef = { key: 'projectId', label: 'Project', control: 'lookup', lookup: { entity: 'project' } }

/** docs/08 §1 — stock moving between the warehouse and a site. */
export const materialTransfer = defineDocument({
  key: 'material_transfer',
  group: 'Inventory',
  blurb: 'Transfer required material from stores to a project site. Also can transfer out from the site.',
  route: 'mat_trfs',
  label: 'Material transfer',
  labelPlural: 'Material transfers',
  numbering: 'MATERIAL_TRANSFER',
  numberPrefix: 'MT-{CO} ',
  workflow: 'MATERIAL_TRANSFER',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Material transfer type',
    subtitle: 'Which way is the material moving, and for which project?',
    fields: [
      f.choice('transferType', 'Type', TRANSFER_TYPES, true, 'full'),
      f.look('projectId', 'Project', 'project', true, 'full'),
    ],
  },
  form: {
    sections: [
      // STK-003: the reference's seven questions, in its order. What we ask
      // beyond them — who carried it, the vehicle, the e-way bill — is in
      // Delivery info, where it belongs.
      { key: 'general', n: '01', title: 'General info', fields: [
        f.choice('transferType', 'Type', TRANSFER_TYPES),
        f.look('projectId', 'Project', 'project', true),
        // INV-006: the project's customer, filled in from the project.
        { ...f.look('customerId', 'Customer', 'customer', true), hint: 'Taken from the project.' },
        f.date('documentDate', 'Document date', true),
        // STK-001: one required question, several stores. The stock picker and
        // the auto-pick take lots from every one named. A site to site move
        // has no warehouse end, so it is not asked there.
        { ...many(f.look('warehouseIds', 'Warehouses', 'warehouse', true)),
          visibleWhen: { field: 'transferType', equals: WAREHOUSE_ENDED } },
        // STK-004: the header Project is where the material goes, as on every
        // other type; a site to site move names the site it comes from.
        { ...f.look('fromProjectId', 'From project', 'project', true),
          visibleWhen: { field: 'transferType', equals: ['SITE_TO_SITE'] },
          hint: 'The site the material leaves. It is taken from that site’s store.' },
        interstate(),
        f.notes(),
        // Kept on the record, never asked (`importOnly`): the first of the
        // warehouses, for the stock, print and report code that reads one;
        // the source site's customer; the job type, from the project.
        { ...f.look('warehouseId', 'Warehouse', 'warehouse'), importOnly: true },
        { ...f.look('fromCustomerId', 'From customer', 'customer'), importOnly: true },
        { ...jobType(), importOnly: true },
        taxUnitChip(),
      ] },
      // STK-006: addresses first, each with its card, then the person.
      { key: 'delivery', n: '02', title: 'Delivery info', fields: [
        scoped(f.look('dispatchAddressId', 'Dispatch address', 'address'), OURS),
        scoped(f.look('deliveryAddressId', 'Delivery address', 'address'), ofPartyOrOurs('customerId'), 'none'),
        // INV-007: a person, whose number fills itself in.
        { ...f.look('deliveryCoordinatorId', 'Delivery coordinator', 'employee'),
          hint: 'Their mobile number fills the contact number when that is empty.' },
        f.text('deliveryContactNo', 'Delivery contact no'),
        f.look('issuedToId', 'Handed to', 'employee'),
        f.text('vehicleNo', 'Vehicle no'),
        // A transfer moves goods on a public road exactly as a challan does,
        // and the setting has always said the number is shown on both. Only
        // the challan had the fields.
        { ...f.text('ewayBillNo', 'e-Way bill no'), requiresSetting: 'ewaybill_enabled' },
        { ...f.choice('ewayBillStatus', 'e-Way bill status', [
          ['', 'Not raised'], ['GENERATED', 'Generated'], ['CANCELLED', 'Cancelled'],
          ['EXPIRED', 'Expired'],
        ], false), requiresSetting: 'ewaybill_enabled' },
      ] },
    ],
  },
  lines: {
    ...quantityLines('Material info', '03', 'Quantity'),
    countAs: { label: 'Items', noun: 'material(s)' },
    /*
      STK-005: the reference's six columns. The two quantity cells ARE the
      quantity — what they add up to is what is stored and moved — and each
      carries the lot it came from as a mark, not a column. A line takes one
      lot, so on save the server puts the whole of it under From reserved or
      From stock, by whether that lot was received for this project. Rate and
      tax still ride on the line (the rate from the lot, the tax from the
      item) and show in the Total Value card, not here.
    */
    columns: [
      col.seq(),
      // One transfer serves several requests, line by line (migration 065).
      { key: 'mrNo', label: 'Material request', type: 'text', width: 150,
        hint: 'The request this line answers. Pick lines from the requests to fill it.' },
      col.item('Item'), col.desc(), col.unit(),
      { key: 'fromReserved', label: 'From reserved', type: 'qty', width: 140, align: 'right', editable: true,
        lotNote: 'lotRef', hint: 'Taken from stock reserved for this project.' },
      { key: 'fromStock', label: 'From stock', type: 'qty', width: 140, align: 'right', editable: true,
        lotNote: 'lotRef', hint: 'Taken from free stock.' },
      { ...col.qty('Quantity'), dialogOnly: true, editable: false,
        formula: { sum: ['fromReserved', 'fromStock'] } },
    ],
    sources: [
      { key: 'request', label: 'Select from material requests', source: 'request' },
      { key: 'reserved', label: 'Select reserved materials', source: 'reserved' },
      { key: 'stock', label: 'Select materials from stock', source: 'stock' },
      { key: 'excel', label: 'Import from Excel', source: 'excel' },
    ],
  },
  // INV-002/003: the reference's default list, then its Create View catalogue.
  columns: [
    docNoColumn,
    docDateColumn,
    { key: 'projectName', label: 'Project', type: 'text' },
    typeColumn('transferType'),
    { key: 'documentStatus', label: 'Status', type: 'status', width: 134 },
    totalColumn(),
    opt('customerName', 'Customer', 'text', 200),
    // STK-019: the reference's catalogue.
    opt('warehouseIds', 'Warehouses', 'text', 220),
    // The first of them, which views saved before STK-001 name.
    opt('warehouseId', 'Warehouse', 'text', 180),
    opt('fromProjectId', 'From project', 'text', 200),
    opt('fromCustomerId', 'From customer', 'text', 200),
    opt('totalAfterTax', 'Total after tax (FCY)', 'money', 160),
    opt('deliveryCoordinatorId', 'Delivery coordinator', 'text', 180),
    opt('deliveryContactNo', 'Delivery contact no', 'text', 150),
    opt('vehicleNo', 'Vehicle no', 'text', 130),
    opt('taxUnitId', 'Tax unit', 'text', 180),
    opt('jobTypeName', 'Job type', 'text', 150),
    opt('interstate', 'Interstate (IGST)?', 'bool', 130),
    // The money column already reads Total (FCY); the currency it is in.
    { key: 'currencyCode', label: 'Currency', type: 'text', width: 100, optional: true },
    ...stockListExtras(),
  ],
  filters: [
    docNoFilter,
    docDateFilter,
    projectFilter,
    { key: 'transferType', label: 'Type', control: 'select', options: choices(TRANSFER_TYPES) },
    { key: 'warehouseId', label: 'Warehouse', control: 'lookup', lookup: { entity: 'warehouse' } },
    submittedBy,
    taxUnitFilter,
    jobTypeFilter,
    statusFilter,
  ],
  // INV-014: Stop sync / Re-sync, behind the integration setting (off by default).
  actions: [...SYNCED_BASIC_ACTIONS, 'changeTaxUnit'],
  // INV-013: the project's allowed brands, through the request line or the estimate.
  rules: ['excess_purchase_quantity', 'insufficient_stock', 'brand_mismatch'],
  requiresSetting: 'inventory_enabled',
  // No `allocatesFrom`: what a transfer answers is per line, picked with
  // "Select from material requests" (STK-003). A header request field that
  // fed "Pull the open lines" competed with the line's own.
  // STK-009, STK-010: the reference's Summary card and Details.
  summaryCard: { title: 'project', line: ['transferType', 'documentDate', 'customerId', 'integration'] },
  summaryDetails: ['jobTypeId', 'taxUnitId', 'remarks', 'interstate', 'currency'],
  createsNext: [{ docType: 'delivery_challan', label: 'Delivery challan', whenStatus: 'APPROVED' }],
} as const)

/** docs/08 §2 — material issued from, or returned to, a site store. */
export const siteInventoryRecord = defineDocument({
  key: 'site_inventory_record',
  group: 'Inventory',
  blurb: 'Create material inward and outward for a project site store.',
  route: 'site_issues',
  label: 'Site inventory record',
  labelPlural: 'Site inventory records',
  numbering: 'SITE_INVENTORY_RECORD',
  numberPrefix: 'Site Inven: ',
  numberWidth: 3,
  workflow: 'SITE_INVENTORY_RECORD',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Site inventory record',
    fields: [
      f.radio('recordType', 'Type?', RECORD_TYPES),
      f.look('projectId', 'Project', 'project', true, 'full'),
    ],
  },
  form: {
    sections: [
      // STK-008: the reference's fields in its order; who it went to, which
      // the reference does not ask, after them.
      { key: 'general', n: '01', title: 'General info', fields: [
        f.radio('recordType', 'Type', RECORD_TYPES),
        f.look('projectId', 'Project', 'project', true),
        { ...f.look('customerId', 'Customer', 'customer', true), hint: 'Taken from the project.' },
        f.look('subcontractorId', 'Subcontractor', 'subcontractor'),
        f.date('documentDate', 'Document date', true),
        f.notes(),
        f.look('issuedToId', 'Issued to', 'employee'),
        // The project's job type, kept on the record and never asked.
        { ...jobType(), importOnly: true },
        taxUnitChip(),
      ] },
    ],
  },
  // The reference records a site issue as quantities against the request the
  // material came for — no rate, no tax. The rate still comes from the lot,
  // so the store's value is right; it is just not this form's business.
  lines: {
    title: 'Materials', n: '02',
    countAs: { label: 'Items', noun: 'Item(s)' },
    columns: [
      col.seq(),
      { key: 'mrNo', label: 'Material request', type: 'text', width: 150,
        hint: 'The request this material was brought to site for, as its lot traces it.' },
      col.item(), col.desc(), col.brand(), col.qty('Quantity'), col.unit(),
    ],
    sources: [
      { key: 'stock', label: 'Select items', source: 'stock' },
      { key: 'excel', label: 'Import from Excel', source: 'excel' },
    ],
  },
  // INV-018.
  columns: [
    docNoColumn,
    docDateColumn,
    { key: 'projectName', label: 'Project', type: 'text' },
    typeColumn('recordType'),
    { key: 'documentStatus', label: 'Status', type: 'status', width: 134 },
    totalColumn(true),
    opt('subcontractorId', 'Subcontractor', 'text', 200),
    opt('customerName', 'Customer', 'text', 200),
    opt('issuedToId', 'Issued to', 'text', 180),
    opt('taxUnitId', 'Tax unit', 'text', 180),
    opt('jobTypeName', 'Job type', 'text', 150),
    opt('remarks', 'Remarks', 'text', 240),
    opt('createdAt', 'Created on', 'date', 130),
  ],
  filters: [
    docNoFilter,
    docDateFilter,
    projectFilter,
    { key: 'recordType', label: 'Type', control: 'select', options: choices(RECORD_TYPES) },
    { key: 'subcontractorId', label: 'Subcontractor', control: 'lookup', lookup: { entity: 'subcontractor' } },
    submittedBy,
    taxUnitFilter,
    jobTypeFilter,
    statusFilter,
  ],
  actions: [...BASIC_ACTIONS, 'changeTaxUnit'],
  // STK-009, STK-010.
  summaryCard: { title: 'party', line: ['recordType', 'documentDate', 'projectId'] },
  summaryDetails: ['jobTypeId', 'taxUnitId', 'remarks'],
  // The company setting says the feature exists at all; the rule says whether
  // THIS project uses it, which is the vendor's own condition
  // (docs/video-notes/06).
  rules: ['site_store_not_tracked', 'insufficient_stock', 'brand_mismatch'],
  requiresSetting: 'site_inventory_enabled',
} as const)

/** Where the part of an issue that was not used went (docs/24 §3.9). */
export const WASTAGE_REASONS = [
  ['NORMAL', 'Normal wastage — off-cuts, handling'],
  ['DAMAGE', 'Damaged at site'],
  ['THEFT', 'Theft'],
  ['LOSS', 'Lost'],
  ['EXCESS_CUTTING', 'Excess cutting'],
] as const satisfies ReadonlyArray<[string, string]>

/**
 * docs/24 §3.9 — what happened to what was issued.
 *
 * "Material issued to site 20 m; actual material used 18 m; balance 2 m — it
 * can be wastage, theft, loss; or it will return to inventory." Each line
 * settles part of an issue: how much went into the work, how much was lost
 * and why, and how much came back to the store. Whatever is not accounted for
 * stays open on the issue as the balance still with the crew, and turns up on
 * the next consumption record.
 *
 * What comes back is stock again — a lot at the site store, or at the
 * warehouse named — and is issued to the next job like anything else. Used
 * and wastage are measured against what the measured work says should have
 * gone in: the work progress (JMR) named on the header.
 */
export const materialConsumption = defineDocument({
  key: 'material_consumption',
  group: 'Inventory',
  blurb: 'Account for what was issued to site: used, wasted (and why), and returned to the store.',
  route: 'mat_consumption',
  label: 'Material consumption',
  labelPlural: 'Material consumption records',
  numbering: 'MATERIAL_CONSUMPTION',
  numberPrefix: 'MC/',
  workflow: 'MATERIAL_CONSUMPTION',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Record material consumption',
    subtitle: 'Choose the project; the issues still to be accounted for can then be picked.',
    fields: [
      f.look('projectId', 'Project', 'project', true, 'full'),
      f.radio('consumedBy', 'Who used it?', [['SUBCONTRACTOR', 'A subcontractor'], ['OWN_LABOUR', 'Our own labour']]),
      { ...f.look('subcontractorId', 'Subcontractor', 'subcontractor', true, 'full'),
        visibleWhen: { field: 'consumedBy', equals: ['SUBCONTRACTOR'] } },
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General information', fields: [
        f.look('projectId', 'Project', 'project', true),
        f.date('documentDate', 'Recorded on', true),
        f.date('periodFrom', 'Period from'),
        f.date('periodTo', 'Period to'),
        f.choice('consumedBy', 'Who used it?', [['SUBCONTRACTOR', 'A subcontractor'], ['OWN_LABOUR', 'Our own labour']], false),
        { ...f.look('subcontractorId', 'Subcontractor', 'subcontractor'),
          visibleWhen: { field: 'consumedBy', equals: ['SUBCONTRACTOR'] } },
        { ...f.look('workProgressId', 'Against measured work (JMR)', 'work_progress'),
          hint: 'The work progress this material went into. What it measured is what should have been used.' },
        { ...f.look('returnWarehouseId', 'Return the balance to', 'warehouse'),
          hint: 'Left empty, what comes back stays in the site store for the next issue.' },
        f.look('recordedById', 'Recorded by', 'employee'),
        jobType(),
        f.notes(),
      ] },
    ],
  },
  lines: {
    title: 'What was issued, and what became of it', n: '02',
    columns: [
      col.seq(), col.item('Material', false), col.desc(),
      { key: 'sourceQty', label: 'Issued', type: 'qty', width: 100, align: 'right' },
      { key: 'openQty', label: 'To account', type: 'qty', width: 110, align: 'right' },
      { key: 'usedQty', label: 'Used', type: 'qty', width: 100, align: 'right', editable: true,
        hint: 'What went into the work.' },
      { key: 'wastageQty', label: 'Wastage', type: 'qty', width: 100, align: 'right', editable: true },
      { key: 'wastageReason', label: 'Why wasted', type: 'text', width: 200, editable: true,
        options: WASTAGE_REASONS.map(([value, label]) => ({ value, label })) },
      { key: 'returnQty', label: 'Returned', type: 'qty', width: 104, align: 'right', editable: true,
        hint: 'What comes back to the store, to be issued again.' },
      { key: 'quantity', label: 'Accounted', type: 'qty', width: 110, align: 'right',
        formula: { sum: ['usedQty', 'wastageQty', 'returnQty'] } },
      { key: 'balanceQty', label: 'Still at site', type: 'qty', width: 110, align: 'right' },
      { key: 'theoreticalQty', label: 'As per JMR', type: 'qty', width: 110, align: 'right' },
      { key: 'wastagePct', label: 'Wastage %', type: 'text', width: 100, align: 'right' },
      col.unit(false), col.rate('Rate', false), col.amount('Value'),
    ],
    sources: [
      { key: 'issue', label: 'Pick from what was issued', source: 'issue' },
      // By item code, against the same issues the picker offers.
      { key: 'excel', label: 'Import from Excel', source: 'excel' },
    ],
  },
  columns: baseColumns(null, 'Value'),
  filters: [
    ...baseFilters(null),
    { key: 'subcontractorId', label: 'Subcontractor', control: 'lookup', lookup: { entity: 'subcontractor' } },
  ],
  actions: BASIC_ACTIONS,
  rules: ['consumption_exceeds_issue', 'wastage_over_allowance'],
  requiresSetting: 'inventory_enabled',
  createsNext: [{ docType: 'material_transfer', label: 'Material transfer', whenStatus: 'APPROVED' }],
} as const)

/** docs/08 §3 — stock moving between two warehouses. */
export const warehouseTransfer = defineDocument({
  key: 'warehouse_transfer',
  group: 'Inventory',
  blurb: 'Transfer materials from one warehouse to another.',
  route: 'warehouse_trfs',
  label: 'Warehouse transfer',
  labelPlural: 'Warehouse transfers',
  numbering: 'WAREHOUSE_TRANSFER',
  numberPrefix: 'WT-',
  workflow: 'WAREHOUSE_TRANSFER',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Warehouse transfer',
    fields: [
      f.look('warehouseId', 'From warehouse', 'warehouse', true, 'full'),
      f.look('toWarehouseId', 'To warehouse', 'warehouse', true, 'full'),
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General information', fields: [
        f.look('warehouseId', 'From warehouse', 'warehouse', true),
        f.look('toWarehouseId', 'To warehouse', 'warehouse', true),
        f.date('documentDate', 'Transfer date', true),
        f.text('vehicleNo', 'Vehicle no'),
        // A transfer moves goods on a public road exactly as a challan does,
        // and the setting has always said the number is shown on both. Only
        // the challan had the fields.
        { ...f.text('ewayBillNo', 'e-Way bill no'), requiresSetting: 'ewaybill_enabled' },
        { ...f.choice('ewayBillStatus', 'e-Way bill status', [
          ['', 'Not raised'], ['GENERATED', 'Generated'], ['CANCELLED', 'Cancelled'],
          ['EXPIRED', 'Expired'],
        ], false), requiresSetting: 'ewaybill_enabled' },
        f.text('deliveryCoordinator', 'Delivery coordinator'),
        taxUnitChip(),
        interstate(),
        f.notes(),
      ] },
    ],
  },
  lines: quantityLines('Materials transferred', '02', 'Transferred'),
  // INV-023.
  columns: [
    docNoColumn,
    docDateColumn,
    { key: 'warehouseId', label: 'From warehouse', type: 'text', width: 190 },
    { key: 'toWarehouseId', label: 'To warehouse', type: 'text', width: 190 },
    { key: 'documentStatus', label: 'Status', type: 'status', width: 134 },
    totalColumn(),
    // STK-019: the reference's catalogue starts with the company.
    opt('companyName', 'Company', 'text', 220),
    opt('interstate', 'Interstate (IGST)?', 'bool', 130),
    opt('taxUnitId', 'Tax unit', 'text', 180),
    opt('vehicleNo', 'Vehicle no', 'text', 130),
    // The money column already reads Total (FCY); the currency it is in.
    { key: 'currencyCode', label: 'Currency', type: 'text', width: 100, optional: true },
    ...stockListExtras(),
  ],
  filters: [
    docNoFilter,
    docDateFilter,
    { key: 'warehouseId', label: 'From warehouse', control: 'lookup', lookup: { entity: 'warehouse' } },
    { key: 'toWarehouseId', label: 'To warehouse', control: 'lookup', lookup: { entity: 'warehouse' } },
    submittedBy,
    statusFilter,
  ],
  actions: [...BASIC_ACTIONS, 'changeTaxUnit'],
  rules: ['insufficient_stock'],
  requiresSetting: 'inventory_enabled',
} as const)

/** docs/08 §4 — the store asking the purchase team to buy stock. */
export const stockPurchaseRequest = defineDocument({
  key: 'stock_purchase_request',
  group: 'Inventory',
  blurb: 'Raise a requisition to the purchase team for stock purchases.',
  route: 'stock_pur_reqs',
  label: 'Stock purchase request',
  labelPlural: 'Stock purchase requests',
  numbering: 'STOCK_PURCHASE_REQUEST',
  numberPrefix: 'SPR-',
  workflow: 'STOCK_PURCHASE_REQUEST',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Stock purchase request',
    fields: [
      f.look('warehouseId', 'Warehouse', 'warehouse', true, 'full'),
      { ...taxUnit(true), width: 'full' as const },
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General information', fields: [
        f.look('warehouseId', 'Warehouse', 'warehouse', true),
        f.date('documentDate', 'Request date', true),
        f.date('requiredDate', 'Required by'),
        taxUnitChip(),
        f.look('requestedById', 'Requested by', 'employee'),
        f.notes(),
      ] },
    ],
  },
  lines: quantityLines('Materials wanted', '02', 'Quantity'),
  // STK-018: a stock request has no project, so the list neither shows nor filters one.
  columns: [
    docNoColumn,
    docDateColumn,
    { key: 'warehouseId', label: 'Warehouse', type: 'text', width: 180 },
    { key: 'documentStatus', label: 'Status', type: 'status', width: 134 },
    { key: 'fulfilmentStatus', label: 'Purchase status', type: 'text', width: 190 },
    totalColumn(true),
    opt('projectName', 'Project', 'text', 200),
    ...stockListExtras(),
  ],
  // INV-025, in the reference's order.
  filters: [
    docNoFilter,
    docDateFilter,
    { key: 'warehouseId', label: 'Warehouse', control: 'lookup', lookup: { entity: 'warehouse' } },
    { key: 'createdById', label: 'Created by', control: 'lookup', lookup: { entity: 'employee' } },
    statusFilter,
    { key: 'fulfilment', label: 'Purchase status', control: 'select', options: [
      { value: 'OPEN', label: 'Still to purchase' },
      { value: 'PARTIAL', label: 'Partly purchased' },
      { value: 'COMPLETE', label: 'Fully purchased' },
    ] },
  ],
  actions: [...BASIC_ACTIONS, 'preClose', 'undoPreClose', 'changeTaxUnit'],
  rules: [],
  requiresSetting: 'inventory_enabled',
  createsNext: [{ docType: 'purchase_order', label: 'Purchase order', whenStatus: 'APPROVED' }],
} as const)

/** Why a count differs from the books (docs/24 §3.10). */
export const VARIANCE_REASONS = [
  ['COUNT_ERROR', 'Miscounted earlier'],
  ['ISSUE_NOT_RECORDED', 'Issued without a record'],
  ['RECEIPT_NOT_RECORDED', 'Received without a record'],
  ['DAMAGE', 'Damaged in store'],
  ['THEFT', 'Theft'],
  ['UNIT_MISMATCH', 'Counted in another unit'],
  ['OTHER', 'Other'],
] as const satisfies ReadonlyArray<[string, string]>

/**
 * docs/24 §3.10 — counting what is actually on the shelf.
 *
 * "Monthly one time or weekly one time should take stock audit." The book says
 * one thing and the rack another, and until somebody counts, the book is
 * believed. A count is taken for one store — a warehouse, or a project's site
 * store — either in full or as a cycle count of one ABC class (A weekly, B
 * monthly, C quarterly, by the company's settings). Each line starts from the
 * book quantity, bin by bin; the counter enters what is there; the variance
 * and its value follow. An approved count then drives the stock adjustment
 * that makes the book agree with the shelf.
 */
export const stockAudit = defineDocument({
  key: 'stock_audit',
  group: 'Inventory',
  blurb: 'Count what is on the shelf, bin by bin, and see where the books are wrong.',
  route: 'stock_audits',
  label: 'Stock audit',
  labelPlural: 'Stock audits',
  numbering: 'STOCK_AUDIT',
  numberPrefix: 'SAU-',
  workflow: 'STOCK_AUDIT',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Count stock',
    subtitle: 'Which store, and all of it or one class of material?',
    fields: [
      f.radio('auditAt', 'Where?', [['WAREHOUSE', 'A warehouse'], ['SITE', 'A project site store']]),
      { ...f.look('warehouseId', 'Warehouse', 'warehouse', true, 'full'),
        visibleWhen: { field: 'auditAt', equals: ['WAREHOUSE'] } },
      { ...f.look('projectId', 'Project', 'project', true, 'full'),
        visibleWhen: { field: 'auditAt', equals: ['SITE'] } },
      f.radio('auditType', 'What is being counted?', [
        ['FULL', 'Everything in the store'],
        ['CYCLE', 'One class — a cycle count'],
      ]),
      { ...f.choice('abcClass', 'Class', [['A', 'A'], ['B', 'B'], ['C', 'C'], ['S', 'S']]),
        visibleWhen: { field: 'auditType', equals: ['CYCLE'] } },
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General information', fields: [
        { ...f.choice('auditAt', 'Where?', [['WAREHOUSE', 'A warehouse'], ['SITE', 'A project site store']], false), lockAfterDraft: true },
        { ...f.look('warehouseId', 'Warehouse', 'warehouse'), lockAfterDraft: true,
          visibleWhen: { field: 'auditAt', equals: ['WAREHOUSE'] } },
        { ...f.look('projectId', 'Project', 'project'), lockAfterDraft: true,
          visibleWhen: { field: 'auditAt', equals: ['SITE'] } },
        f.date('documentDate', 'Counted on', true),
        f.choice('auditType', 'Count', [['FULL', 'Full count'], ['CYCLE', 'Cycle count of one class']], false),
        { ...f.choice('abcClass', 'Class', [['A', 'A'], ['B', 'B'], ['C', 'C'], ['S', 'S']], false),
          visibleWhen: { field: 'auditType', equals: ['CYCLE'] } },
        f.look('countedById', 'Counted by', 'employee', true),
        f.look('witnessedById', 'Witnessed by', 'employee'),
        { key: 'accuracyPct', label: 'Lines that agreed', control: 'text', width: 'half',
          hint: 'Worked out on save: the share of lines where the count matched the books.' },
        { key: 'varianceValue', label: 'Net variance value', control: 'text', width: 'half',
          hint: 'Worked out on save: gains less losses, at the book rate.' },
        f.notes(),
      ] },
    ],
  },
  lines: {
    title: 'Counted', n: '02',
    columns: [
      col.seq(), col.item('Material', false), col.desc(),
      { key: 'binCode', label: 'Bin', type: 'text', width: 110 },
      { key: 'bookQty', label: 'Book', type: 'qty', width: 104, align: 'right' },
      { ...col.qty('Counted'), hint: 'What is physically there. Fill all open quantities starts every line at the book figure.' },
      { key: 'varianceQty', label: 'Variance', type: 'qty', width: 104, align: 'right',
        formula: { subtract: ['quantity', 'bookQty'] } },
      col.unit(false),
      col.rate('Book rate', false),
      { key: 'varianceValue', label: 'Variance value', type: 'money', width: 140, align: 'right' },
      { key: 'varianceReason', label: 'Why it differs', type: 'text', width: 200, editable: true,
        options: VARIANCE_REASONS.map(([value, label]) => ({ value, label })) },
    ],
    sources: [
      { key: 'book', label: 'Load the book stock', source: 'book' },
      // The count sheet: the book stock printed out, the counted column filled
      // in on the floor, and brought back in against the same lines.
      { key: 'excel', label: 'Import the count from Excel', source: 'excel' },
    ],
  },
  columns: [
    ...baseColumns(null, 'Counted value'),
    { key: 'auditType', label: 'Count', type: 'text', width: 110 },
    { key: 'accuracyPct', label: 'Agreed', type: 'text', width: 100, align: 'right' },
    { key: 'varianceValue', label: 'Variance', type: 'text', width: 130, align: 'right' },
  ],
  filters: [
    ...baseFilters(null),
    { key: 'warehouseId', label: 'Warehouse', control: 'lookup', lookup: { entity: 'warehouse' } },
  ],
  actions: BASIC_ACTIONS,
  rules: ['variance_without_reason'],
  requiresSetting: 'inventory_enabled',
  createsNext: [{ docType: 'stock_adjustment', label: 'Stock adjustment', whenStatus: 'APPROVED' }],
} as const)

/** docs/08 §5 — correcting what the system thinks is on the shelf. */
export const stockAdjustment = defineDocument({
  key: 'stock_adjustment',
  group: 'Inventory',
  blurb: 'Use stock adjustment to set up all the warehouses with proper inventory.',
  route: 'stock_adjs',
  label: 'Stock adjustment',
  labelPlural: 'Stock adjustments',
  numbering: 'STOCK_ADJUSTMENT',
  numberPrefix: 'SA-',
  numberWidth: 3,
  workflow: 'STOCK_ADJUSTMENT',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Stock adjustment',
    subtitle: 'A warehouse — or, for a count at a site, the project whose site store it is.',
    fields: [
      f.radio('adjustmentType', 'Type', ADJUSTMENT_TYPES),
      f.look('warehouseId', 'Warehouse', 'warehouse', false, 'full'),
      f.look('projectId', 'Site store of project', 'project', false, 'full'),
    ],
  },
  form: {
    sections: [
      // STK-011: Document date, Type, Warehouse, Remarks — the reference's
      // four — with our site-store and stock-audit questions before Remarks.
      // Remarks is the why; the old Reason is kept, no longer demanded.
      { key: 'general', n: '01', title: 'General info', fields: [
        f.date('documentDate', 'Document date', true),
        { ...f.radio('adjustmentType', 'Type', ADJUSTMENT_TYPES),
          width: 'half' as const,
          lockAfterDraft: true,
          hint: 'An increase brings stock on; a decrease takes it off, and must be picked from what is on hand.' },
        { ...f.look('warehouseId', 'Warehouse', 'warehouse'),
          lockAfterDraft: true,
          hint: 'Leave it empty to adjust a project’s site store, and name the project.' },
        { ...f.look('projectId', 'Site store of project', 'project'),
          hint: 'For a count taken at a project site.' },
        // docs/24 §3.10 — the count this adjustment makes the books agree with.
        { ...f.look('stockAuditId', 'From stock audit', 'stock_audit'),
          hint: 'Name the approved count, then load its variances.' },
        f.text('reason', 'Reason', 'full'),
        f.notes(),
      ] },
    ],
  },
  lines: {
    title: 'Materials adjusted', n: '02',
    columns: [col.seq(), col.item(), col.desc(), col.brand(), col.qty('Adjust by'), col.unit(), col.rate('Value rate'), col.bin(), col.amount()],
    // STK-012: "Select materials", and the template both ways.
    sources: [
      { key: 'stock', label: 'Select materials', source: 'stock' },
      { key: 'audit', label: 'Load the audit’s variances', source: 'audit' },
      { key: 'excel', label: 'Import template', source: 'excel', templateLabel: 'Export template' },
    ],
  },
  // INV-026: why stock was adjusted is what people read the list for — the
  // remarks, as the reference has it (STK-011); an older Reason stays optional.
  columns: [
    docNoColumn,
    docDateColumn,
    typeColumn('adjustmentType'),
    { key: 'warehouseId', label: 'Warehouse', type: 'text', width: 180 },
    { key: 'documentStatus', label: 'Status', type: 'status', width: 134 },
    { key: 'remarks', label: 'Remarks', type: 'text', width: 260 },
    totalColumn(),
    opt('projectName', 'Project', 'text', 200),
    opt('reason', 'Reason', 'text', 260),
    { key: 'currencyCode', label: 'Currency', type: 'text', width: 100, optional: true },
    opt('forexRate', 'Forex rate', 'rate', 110),
    ...stockListExtras(),
  ],
  filters: [
    docNoFilter,
    docDateFilter,
    { key: 'warehouseId', label: 'Warehouse', control: 'lookup', lookup: { entity: 'warehouse' } },
    { key: 'adjustmentType', label: 'Type', control: 'select', options: choices(ADJUSTMENT_TYPES) },
    submittedBy,
    projectFilter,
    statusFilter,
  ],
  actions: BASIC_ACTIONS,
  rules: ['insufficient_stock', 'stock_location_missing', 'adjustment_exceeds_count'],
  requiresSetting: 'inventory_enabled',
} as const)
